import { useMemo } from "react";
import { IconFileText } from "@tabler/icons-react";
import { useEmailTemplateVariablesQuery } from "../api/queries/emailTemplateQueries";
import {
  buildPreviewSubject,
  renderTemplatePreview,
  type PreviewMode,
} from "../utils/emailTemplatePreview";

function containsHebrew(text: string): boolean {
  if (!text) return false;
  return /[\u0590-\u05FF\uFB1D-\uFDFD\uFE70-\uFEFC]/.test(text);
}

function wrapPreviewHtml(html: string, isRtl: boolean): string {
  if (!html || !isRtl) return html;
  if (/<[^>]+dir\s*=\s*["']rtl/i.test(html)) return html;
  return `<div dir="rtl" style="direction: rtl; text-align: right;">${html}</div>`;
}

interface EmailTemplatePreviewPanelProps {
  subject: string;
  body: string;
  previewMode?: PreviewMode;
  invoiceListFormat?: string;
}

export function EmailTemplatePreviewPanel({
  subject,
  body,
  previewMode = "single",
  invoiceListFormat,
}: EmailTemplatePreviewPanelProps) {
  const { data: variables = [] } = useEmailTemplateVariablesQuery();

  const isRtl = useMemo(
    () => containsHebrew(subject) || containsHebrew(body),
    [subject, body],
  );

  const previewSubject = useMemo(
    () => buildPreviewSubject(subject, previewMode, variables, isRtl),
    [subject, previewMode, variables, isRtl],
  );

  const previewBody = useMemo(() => {
    const rendered =
      renderTemplatePreview(
        body,
        previewMode,
        variables,
        isRtl,
        invoiceListFormat,
      ) || (isRtl ? "<i>לא הוזן תוכן</i>" : "<i>No content provided</i>");
    return wrapPreviewHtml(rendered, isRtl);
  }, [body, previewMode, variables, isRtl, invoiceListFormat]);

  return (
    <div className="rounded-lg border border-dashed border-gray-300 bg-[#f8f9fa] p-4">
      <div className="space-y-3">
        <div className="flex items-start gap-2">
          <span className="w-14 shrink-0 text-xs font-bold text-gray-500">
            Subject:
          </span>
          <p
            className="text-sm font-medium text-gray-800"
            dir={isRtl ? "rtl" : "ltr"}
            style={{ textAlign: isRtl ? "right" : "left" }}
          >
            {previewSubject}
          </p>
        </div>
        <hr className="border-dashed border-gray-300" />
        <div
          dir={isRtl ? "rtl" : "ltr"}
          lang={isRtl ? "he" : undefined}
          className="max-h-[200px] overflow-y-auto whitespace-pre-wrap px-1 text-sm leading-relaxed text-gray-700"
          style={{
            direction: isRtl ? "rtl" : "ltr",
            textAlign: isRtl ? "right" : "left",
          }}
          dangerouslySetInnerHTML={{ __html: previewBody }}
        />
        <hr className="border-dashed border-gray-300" />
        <div className="space-y-1">
          <p className="text-xs font-bold text-gray-500">Attachments:</p>
          <div className="inline-flex items-center gap-1.5 rounded border border-gray-200 bg-white px-2 py-1">
            <IconFileText size={14} className="text-red-500" />
            <span className="text-xs font-medium text-gray-700">
              invoice-1042.pdf
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
